import React, { startTransition, useState, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop, isEmpty, get } from 'lodash-es';
import { Row, Col } from 'reactstrap';
/* Constants */
import {
  VALIDATORS,
  SSN_FIELD_MASK,
  DATE_OF_BIRTH_MASK,
  PHONE_MASK,
  DIGIT_CHAR,
} from 'client/site-modules/financing/constants/validation';
/* Components */
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { FormFieldMask } from 'site-modules/shared/components/form-field-mask/form-field-mask';
import { FormFieldErrorMessage } from 'site-modules/financing/components/shared/form-field-error-message/form-field-error-message';
/* Tracking */
import { EventToolbox } from 'client/utils/event-toolbox';
import { FINANCING_EVENTS } from 'site-modules/shared/constants/financing/financing-events';
import 'site-modules/financing/components/personal-screen/personal-screen.scss';

export function PersonalScreenForm({
  headlineText = null,
  instructionText = 'All fields are required.',
  hideLegend = false,
  setIsValid = noop,
  isPrimary = false,
  predefinedFields = {},
  setFields = null,
  isReview = false,
  validationInfo = {},
  dataTrackingParent = null,
  isProdEnv = false,
  enableNextBtn = noop,
  setIsFormReady = noop,
  formFields = {},
  areSsnEqual = false,
  setAreSsnEqual = noop,
}) {
  const {
    firstName,
    firstNamePrimary,
    lastName,
    lastNamePrimary,
    email,
    emailPrimary,
    confirmEmail, // TODO: Remove when shop-4713-prequal2 test is cleaned up (revert defaultValue below to emailPrimary/email)
    confirmEmailPrimary,
    phoneNumber,
    phoneNumberPrimary,
    ssn = '',
    ssnPrimary = '',
    dateBirth,
    dateBirthPrimary,
  } = predefinedFields;
  const defaultSsn = isPrimary ? ssnPrimary : ssn;
  const defaultValues = {
    firstName: (isPrimary ? firstNamePrimary : firstName) || '',
    lastName: (isPrimary ? lastNamePrimary : lastName) || '',
    phoneNumber: (isPrimary ? phoneNumberPrimary : phoneNumber) || '',
    email: (isPrimary ? emailPrimary : email) || '',
    confirmEmail: (isPrimary ? confirmEmailPrimary : confirmEmail) || '',
    ssn: defaultSsn,
    dateBirth: (isPrimary ? dateBirthPrimary : dateBirth) || '',
  };
  const [ssnMaskedValue, setSsnMaskedValue] = useState(defaultSsn && defaultSsn.replace(/\d/g, 'X'));
  const [isTouched, setIsTouched] = useState(false);
  const [isFieldUpdated, setIsFieldUpdated] = useState(false);
  const { emailValid, emailValidPrimary, phoneValid, phoneValidPrimary } = validationInfo;
  const [primaryName, primaryId] = useMemo(() => (isPrimary ? ['Primary', '-primary'] : ['', '']), [isPrimary]);

  const methods = useForm({
    defaultValues,
  });
  const {
    formState: { errors: validationErrors, isValid: isRHFValid },
    control,
    getValues,
    reset,
    setValue,
    trigger,
  } = methods;

  const getFormFields = () => ({
    [`firstName${primaryName}`]: getValues('firstName'),
    [`lastName${primaryName}`]: getValues('lastName'),
    [`email${primaryName}`]: getValues('email'),
    [`confirmEmail${primaryName}`]: getValues('confirmEmail'),
    [`phoneNumber${primaryName}`]: getValues('phoneNumber'),
    [`ssn${primaryName}`]: getValues('ssn'),
    [`dateBirth${primaryName}`]: getValues('dateBirth'),
  });

  useEffect(() => {
    if (!isEmpty(predefinedFields)) {
      reset(defaultValues); // RHF reads defaultValues only on initialization, so reset the form when predefined fields change.
      trigger();
      startTransition(() => {
        setSsnMaskedValue(defaultSsn.replace(/\d/g, 'X'));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predefinedFields]);

  /* eslint-disable react-hooks/set-state-in-effect -- form validity must synchronously enable navigation after blur */
  useEffect(() => {
    setIsValid(isRHFValid && !areSsnEqual);

    if (isFieldUpdated) {
      setFields(getFormFields());
      enableNextBtn(isRHFValid && !areSsnEqual);
      setIsFieldUpdated(false);
      setIsFormReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryName, isFieldUpdated, areSsnEqual, isRHFValid]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const formatSsn = value => {
    if (value.length === 9 && !value.includes('-')) {
      return `${value.substr(0, 3)}-${value.substr(3, 2)}-${value.substr(5, 4)}`;
    }
    return value;
  };

  const handleBlur = async (e, name) => {
    enableNextBtn(false);
    setIsFormReady(false);
    let isValidated;

    if (name === 'ssn') {
      const formattedSsnValue = formatSsn(getValues('ssn'));

      setValue('ssn', formattedSsnValue);
      isValidated = await trigger('ssn');
      setSsnMaskedValue(formattedSsnValue.replace(/\d/g, 'X'));
      setIsTouched(true);

      if (!isEmpty(formFields) || !isEmpty(predefinedFields)) {
        const ssnFieldValue = isPrimary
          ? get(formFields, 'ssn', get(predefinedFields, 'ssn'))
          : get(formFields, 'ssnPrimary', get(predefinedFields, 'ssnPrimary'));

        const areEqual = formattedSsnValue === ssnFieldValue;

        setAreSsnEqual(areEqual);
        isValidated = isValidated && !areEqual;
      }
    } else {
      isValidated = await trigger(name);

      if (name === 'email') {
        await trigger('confirmEmail');
      }
    }

    setIsFieldUpdated(true);

    EventToolbox.fireCustomEvent(FINANCING_EVENTS.INPUT_VALIDATION, {
      target: e.target,
      data: {
        isValidated,
        isPrimary,
        name,
      },
    });
  };

  const handleFocus = name => {
    setIsFieldUpdated(false);
    setIsFormReady(false);
    enableNextBtn(false);

    if (name === 'ssn' && isTouched) {
      setSsnMaskedValue(getValues('ssn'));
    }
  };

  const handleChange = (value, onChange) => {
    const isDigitsOnly = DIGIT_CHAR.test(value);
    if (isDigitsOnly) {
      onChange(value);
    }
  };

  const isConfirmEmailValid = !validationErrors.confirmEmail;
  const isEmailValid = isPrimary ? emailValidPrimary : emailValid;
  const isPhoneNumberValid = isPrimary ? phoneValidPrimary : phoneValid;

  return (
    <FormProvider {...methods}>
      <fieldset data-tracking-parent={dataTrackingParent}>
        <legend className={`size-12 text-gray-darker mb-1_5 ${isReview ? 'text-start' : 'text-center'}`}>
          {headlineText ? (
            <span className={classnames('d-block size-16 fw-bold mb-0_25', { 'visually-hidden': hideLegend })}>
              {headlineText}
            </span>
          ) : null}
          {instructionText}
        </legend>
        <Row>
          <Col md={6}>
            <Controller
              name="firstName"
              control={control}
              rules={{
                validate: value => (value && VALIDATORS.firstName.test(value)) || VALIDATORS.firstName.errorText,
              }}
              render={({ field, fieldState: { invalid, error } }) => (
                <>
                  <FormField
                    {...field}
                    id={`first-name${primaryId}`}
                    className={classnames({ 'mb-0': invalid })}
                    type="text"
                    maxLength={255}
                    inputClassName="input-field"
                    isValid={!invalid}
                    onBlur={event => {
                      field.onBlur();
                      handleBlur(event, field.name);
                    }}
                    onFocus={handleFocus}
                    placeholder="First Name"
                    ariaInvalid={invalid}
                    ariaDescribedBy={`first-name${primaryId}-errorMessage`}
                    label="First Name"
                    useFloatingLabel
                    autoComplete="given-name"
                    aria-required
                  />
                  <FormFieldErrorMessage
                    id={`first-name${primaryId}-errorMessage`}
                    message={error?.message}
                    className="mt-0_25 mb-1 ps-1"
                  />
                </>
              )}
            />
          </Col>
          <Col md={6}>
            <Controller
              name="lastName"
              control={control}
              rules={{
                validate: value => (value && VALIDATORS.lastName.test(value)) || VALIDATORS.lastName.errorText,
              }}
              render={({ field, fieldState: { invalid, error } }) => (
                <>
                  <FormField
                    {...field}
                    id={`last-name${primaryId}`}
                    className={classnames({ 'mb-0': invalid })}
                    type="text"
                    maxLength={255}
                    inputClassName="input-field"
                    isValid={!invalid}
                    onBlur={event => {
                      field.onBlur();
                      handleBlur(event, field.name);
                    }}
                    onFocus={handleFocus}
                    placeholder="Last Name"
                    ariaInvalid={invalid}
                    ariaDescribedBy={`last-name${primaryId}-errorMessage`}
                    label="Last Name"
                    useFloatingLabel
                    autoComplete="family-name"
                    aria-required
                  />
                  <FormFieldErrorMessage
                    id={`last-name${primaryId}-errorMessage`}
                    message={error?.message}
                    className="mt-0_25 mb-1 ps-1"
                  />
                </>
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Controller
              name="phoneNumber"
              control={control}
              rules={{
                validate: value => (value && VALIDATORS.phoneNumber.test(value)) || VALIDATORS.phoneNumber.errorText,
              }}
              render={({ field, fieldState: { invalid, error } }) => (
                <>
                  <FormFieldMask
                    {...field}
                    id={`tel${primaryId}`}
                    className={classnames({ 'mb-0': invalid })}
                    type="tel"
                    inputClassName="input-field"
                    isValid={!invalid && isPhoneNumberValid}
                    onBlur={event => {
                      field.onBlur();
                      handleBlur(event, field.name);
                    }}
                    onFocus={handleFocus}
                    mask={PHONE_MASK}
                    defaultValue={field.value}
                    ariaInvalid={invalid}
                    ariaDescribedBy={`tel${primaryId}-errorMessage`}
                    label="Phone Number"
                    useFloatingLabel
                    autoComplete="tel-national"
                    aria-required
                    inputMode="numeric"
                  />
                  <FormFieldErrorMessage
                    id={`tel${primaryId}-errorMessage`}
                    message={error?.message}
                    className="mt-0_25 mb-1 ps-1"
                  />
                </>
              )}
            />
            <Controller
              name="email"
              control={control}
              rules={{
                validate: value => (value && VALIDATORS.email.test(value)) || VALIDATORS.email.errorText,
              }}
              render={({ field, fieldState: { invalid, error } }) => (
                <>
                  <FormField
                    {...field}
                    id={`email${primaryId}`}
                    className={classnames({ 'mb-0': invalid })}
                    type="email"
                    maxLength={255}
                    inputClassName="input-field"
                    isValid={!invalid && isEmailValid}
                    onBlur={event => {
                      field.onBlur();
                      handleBlur(event, field.name);
                    }}
                    onFocus={handleFocus}
                    placeholder="Email Address†"
                    ariaInvalid={invalid}
                    ariaDescribedBy={`email${primaryId}-errorMessage`}
                    ariaDescribedByAdditional="email-agreement"
                    label="Email Address†"
                    useFloatingLabel
                    autoComplete="email"
                    aria-required
                  />
                  <FormFieldErrorMessage
                    id={`email${primaryId}-errorMessage`}
                    message={error?.message}
                    className="mt-0_25 mb-1 ps-1"
                  />
                </>
              )}
            />
            <Controller
              name="confirmEmail"
              control={control}
              rules={{
                validate: value => {
                  if (!VALIDATORS.confirmEmail.test(value)) {
                    return VALIDATORS.confirmEmail.errorText;
                  }

                  return value === getValues('email') || 'Please check that your email is entered correctly.';
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <FormField
                    {...field}
                    id={`confirm-email${primaryId}`}
                    className={classnames({ 'mb-0': !isConfirmEmailValid })}
                    type="email"
                    maxLength={255}
                    inputClassName="input-field"
                    isValid={isConfirmEmailValid && isEmailValid}
                    onBlur={event => {
                      field.onBlur();
                      handleBlur(event, field.name);
                    }}
                    onFocus={handleFocus}
                    placeholder="Confirm Email Address†"
                    ariaInvalid={!isConfirmEmailValid}
                    ariaDescribedBy={`confirm-email${primaryId}-errorMessage`}
                    ariaDescribedByAdditional="email-agreement"
                    label="Confirm Email Address†"
                    useFloatingLabel
                    autoComplete="email"
                    aria-required
                  />
                  <FormFieldErrorMessage
                    id={`confirm-email${primaryId}-errorMessage`}
                    message={error?.message}
                    className="mt-0_25 mb-1 ps-1"
                  />
                </>
              )}
            />
            {!isReview && (
              <div className="credit-score-disclaimer d-flex px-1 py-1 mb-1_25">
                <span className="icon-shield-check size-20 align-self-center m-0 pe-0_5" aria-hidden />
                <div className="size-14">
                  Don&apos;t worry, this will <strong>not impact your credit score.</strong>
                </div>
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col md={7} className="ssn-field pos-r">
            <Controller
              name="ssn"
              control={control}
              rules={{
                validate: value =>
                  (isProdEnv ? validation.validateSSN(value) : validation.validateSimpleSSN(value)) ||
                  'Please enter a valid social security number',
              }}
              render={({ field, fieldState: { invalid, error } }) => (
                <>
                  <FormFieldMask
                    {...field}
                    id={`ssn${primaryName}`}
                    type="text"
                    inputClassName="input-field mb-0_5 ps-3"
                    isValid={!invalid && !areSsnEqual}
                    onBlur={event => {
                      field.onBlur();
                      handleBlur(event, field.name);
                    }}
                    onFocus={handleFocus}
                    onChange={value => handleChange(value, field.onChange)}
                    mask={SSN_FIELD_MASK}
                    defaultValue={ssnMaskedValue}
                    isTouched={isTouched}
                    label="Social Security Number"
                    labelClassName="ms-1_75"
                    useFloatingLabel
                    ariaInvalid={invalid || areSsnEqual}
                    ariaDescribedBy={`ssn${primaryId}-errorMessage`}
                    ariaDescribedByAdditional={`ssn${primaryId}-helpText`}
                    addon={
                      <div
                        className="size-14 ms-1 pos-a form-field-label form-control-label"
                        id={`ssn${primaryId}-helpText`}
                      >
                        XXX-XX-XXXX
                      </div>
                    }
                    autoComplete="off"
                    aria-required
                    inputMode="numeric"
                  />
                  <i className="icon-lock2 ssn-lock-icon size-20" aria-hidden />
                  <FormFieldErrorMessage
                    id={`ssn${primaryId}-errorMessage`}
                    message={
                      areSsnEqual ? 'Social security numbers of co-applicants cannot be identical' : error?.message
                    }
                    className="mt-1_75 mb-1 ps-1"
                  />
                </>
              )}
            />
          </Col>

          <Col md={5}>
            <Controller
              name="dateBirth"
              control={control}
              rules={{
                validate: value => (value && VALIDATORS.dateBirth.test(value)) || VALIDATORS.dateBirth.errorText,
              }}
              render={({ field, fieldState: { invalid, error } }) => (
                <>
                  <FormFieldMask
                    {...field}
                    id={`date-birth${primaryId}`}
                    type="text"
                    className="mt-2 mt-md-0"
                    inputClassName="input-field mb-0_5"
                    isValid={!invalid}
                    mask={DATE_OF_BIRTH_MASK}
                    onBlur={event => {
                      field.onBlur();
                      handleBlur(event, field.name);
                    }}
                    onFocus={handleFocus}
                    defaultValue={field.value}
                    ariaInvalid={invalid}
                    ariaDescribedBy={`date-birth${primaryId}-errorMessage`}
                    ariaDescribedByAdditional={`date-birth${primaryId}-helpText`}
                    label="Date of Birth"
                    useFloatingLabel
                    addon={
                      <div
                        className="size-14 ms-1 pos-a form-field-label form-control-label"
                        id={`date-birth${primaryId}-helpText`}
                      >
                        YYYY-MM-DD
                      </div>
                    }
                    autoComplete="bday-day"
                    aria-required
                    inputMode="numeric"
                  />
                  <FormFieldErrorMessage
                    id={`date-birth${primaryId}-errorMessage`}
                    message={error?.message}
                    className="mt-1_75 mb-1 ps-1"
                  />
                </>
              )}
            />
          </Col>
        </Row>
      </fieldset>
    </FormProvider>
  );
}

PersonalScreenForm.propTypes = {
  headlineText: PropTypes.string,
  instructionText: PropTypes.string,
  hideLegend: PropTypes.bool,
  setIsValid: PropTypes.func,
  isPrimary: PropTypes.bool,
  predefinedFields: PropTypes.shape({
    firstName: PropTypes.string,
    firstNamePrimary: PropTypes.string,
    lastName: PropTypes.string,
    lastNamePrimary: PropTypes.string,
    email: PropTypes.string,
    emailPrimary: PropTypes.string,
    confirmEmail: PropTypes.string,
    confirmEmailPrimary: PropTypes.string,
    phoneNumber: PropTypes.string,
    phoneNumberPrimary: PropTypes.string,
    ssn: PropTypes.string,
    ssnPrimary: PropTypes.string,
    dateBirth: PropTypes.string,
    dateBirthPrimary: PropTypes.string,
  }),
  validationInfo: PropTypes.shape({
    emailValid: PropTypes.bool,
    emailValidPrimary: PropTypes.bool,
    phoneValid: PropTypes.bool,
    phoneValidPrimary: PropTypes.bool,
  }),
  setFields: PropTypes.func,
  isReview: PropTypes.bool,
  dataTrackingParent: PropTypes.string,
  isAllStepsSkipped: PropTypes.bool,
  isProdEnv: PropTypes.bool,
  enableNextBtn: PropTypes.func,
  setIsFormReady: PropTypes.func,
  formFields: PropTypes.shape({}),
  areSsnEqual: PropTypes.bool,
  setAreSsnEqual: PropTypes.func,
};
